/**
 * IFSC codes — the 11-character identifier of an Indian bank branch.
 *
 * Shape: 4 letters (the bank), a reserved '0', then 6 alphanumerics (the
 * branch). They are written in upper case, so input is normalised here rather
 * than at every caller.
 *
 * Lookups go to Razorpay's public IFSC service. Two things about that call:
 *
 *  • It uses fetch, NOT axios. src/api/api.js installs a GLOBAL axios request
 *    interceptor that pins the user's `Authorization: Bearer <jwt>` onto every
 *    axios request, and sets `axios.defaults.withCredentials = true`. An axios
 *    call to a third-party host would therefore hand that host the session
 *    token and our cookies. fetch with `credentials: "omit"` sends neither.
 *
 *  • The response is text written by somebody else. It is rendered as a React
 *    text node — which escapes it — and never through dangerouslySetInnerHTML;
 *    cleanField below also collapses whitespace and caps the length so a
 *    malformed payload cannot stretch the layout.
 *
 * If a Content-Security-Policy is ever added to this app, `connect-src` has to
 * include https://ifsc.razorpay.com or the lookup silently stops working.
 */

export const IFSC_LENGTH = 11;
export const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;

const LOOKUP_ORIGIN = "https://ifsc.razorpay.com";
// A lookup is a convenience; it must not hang the field if the service is
// unreachable in a way that never resolves.
const LOOKUP_TIMEOUT_MS = 8000;
const MAX_FIELD_CHARS = 120;

/** Anything typed or pasted → the canonical form: upper case, alphanumerics
 *  only (so "sbin 0001234" survives a paste), never longer than a code. */
export const normalizeIfsc = (value) =>
  String(value ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, IFSC_LENGTH);

export const isValidIfsc = (value) => IFSC_RE.test(normalizeIfsc(value));

/**
 * Failures the field has to tell apart:
 *   invalid       — not an IFSC at all; never left the browser
 *   not-found     — the service has no such branch (404)
 *   network       — unreachable, timed out, or a 5xx: worth retrying
 *   bad-response  — reached it, but the payload made no sense
 */
const lookupError = (kind, message) =>
  Object.assign(new Error(message), { name: "IfscLookupError", kind });

const cleanField = (value) =>
  typeof value === "string" || typeof value === "number"
    ? String(value).replace(/\s+/g, " ").trim().slice(0, MAX_FIELD_CHARS)
    : "";

/**
 * One IFSC → { ifsc, branch, bank, city }. Throws a lookupError otherwise.
 *
 * @param {string} code
 * @param {{ signal?: AbortSignal, fetchImpl?: typeof fetch }} [options]
 */
export async function lookupIfsc(code, { signal, fetchImpl } = {}) {
  const ifsc = normalizeIfsc(code);
  if (!IFSC_RE.test(ifsc)) {
    throw lookupError("invalid", "That is not a valid IFSC code.");
  }

  const doFetch =
    fetchImpl ||
    (typeof window !== "undefined" && typeof window.fetch === "function"
      ? window.fetch.bind(window)
      : null);
  if (!doFetch) {
    throw lookupError("network", "Lookups are not available in this browser.");
  }

  // Our own controller so the timeout and the caller's signal can both stop
  // the request, while staying distinguishable afterwards.
  const controller = new AbortController();
  const relayAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", relayAbort);
  }
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, LOOKUP_TIMEOUT_MS);

  let response;
  try {
    response = await doFetch(`${LOOKUP_ORIGIN}/${ifsc}`, {
      signal: controller.signal,
      credentials: "omit",
      headers: { Accept: "application/json" },
    });
  } catch (err) {
    if (timedOut) {
      throw lookupError("network", "The lookup timed out.");
    }
    // The caller abandoned this code (they typed another one) — that is not a
    // failure to report, so the original AbortError goes back up untouched.
    if (signal?.aborted) throw err;
    throw lookupError("network", "Could not reach the lookup service.");
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", relayAbort);
  }

  if (response.status === 404) {
    throw lookupError("not-found", "No branch found for this IFSC code.");
  }
  if (!response.ok) {
    throw lookupError("network", `The lookup service returned ${response.status}.`);
  }

  let data;
  try {
    data = await response.json();
  } catch {
    throw lookupError("bad-response", "The lookup service sent an unreadable reply.");
  }
  if (!data || typeof data !== "object") {
    throw lookupError("bad-response", "The lookup service sent an unreadable reply.");
  }

  const branch = cleanField(data.BRANCH);
  const bank = cleanField(data.BANK);
  const city = cleanField(data.CITY);
  // DISTRICT and STATE are what place a branch for someone who knows the area —
  // SBIN0070610 is KOORIYAD in MALAPPURAM, KERALA, where CITY says "MALAPURAM".
  const district = cleanField(data.DISTRICT);
  const state = cleanField(data.STATE);
  // The service answers 200 with a body for codes it half-knows; without a
  // branch or a bank there is nothing to show, which is a miss, not an error.
  if (!branch && !bank) {
    throw lookupError("not-found", "No branch found for this IFSC code.");
  }

  return { ifsc, branch, district, state, bank, city };
}
